import type Lenis from 'lenis';

// CHECK TOUCHE DEVICE
export const isTouch = (): boolean => {
    return document.documentElement.classList.contains('touchEnabled');
};

// CHECK LARGE LAPTOP
export const isLargeLaptop = (): boolean => {
    return window.matchMedia('(min-width: 1201px)').matches;
};

// CHECK LARGE DESKTOP
export const isLargeDesktop = (): boolean => {
    return window.matchMedia('(min-width: 1536px)').matches;
};

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>): void => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
};

// check if scrolled
export const isScrolled = (lenisInstance: Lenis, threshold: number = 2, delay: number = 50): void => {
    const body = document.body;
    let checkScrolled = false;

    const checkScrollProgress = debounce(() => {
        const scrollProgress = Math.round(lenisInstance.progress * 100);
        const newIsScrolled = scrollProgress >= threshold;

        if (newIsScrolled && !checkScrolled) {
            body.classList.add('is-scrolled');
            checkScrolled = true;
        } else if (!newIsScrolled && checkScrolled) {
            body.classList.remove('is-scrolled');
            checkScrolled = false;
        }
    }, delay);

    lenisInstance.on('scroll', checkScrollProgress);
};

// check if is bottom
export const isBottom = (lenisInstance: Lenis, threshold: number = 100): void => {
    let checkBottom = false;

    const checkScrollPosition = (): void => {
        const scrollPosition = lenisInstance.scroll;
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const newIsBottom = (scrollPosition + windowHeight >= documentHeight - threshold);

        if (newIsBottom && !checkBottom) {
            document.body.classList.add('is-bottom');
            checkBottom = true;
        } else if (!newIsBottom && checkBottom) {
            document.body.classList.remove('is-bottom');
            checkBottom = false;
        }
    };

    lenisInstance.on('scroll', checkScrollPosition);
};

// OBFUSCATION
export const obfuscation = (): void => {
    const obf = document.querySelectorAll<HTMLElement>('.obf-link');

    obf.forEach((element) => {
        element.addEventListener('click', function(this: HTMLElement): void {
            const link = atob(this.getAttribute('data-obf') || '');
            const target = this.getAttribute('data-target');

            if (target === '_blank') {
                window.open(link, '_blank');
            } else {
                window.location.href = link;
            }
        });
    });
};

// WINDOW HEIGHT
export const windowHeight = (): void => {
    const updateHeight = (): void => {
        document.documentElement.style.setProperty('--window-innerHeight', `${window.innerHeight}px`);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
};

// WINDOW WIDTH
export const windowWidth = (): void => {
    const updateWidth = (): void => {
        document.documentElement.style.setProperty('--window-innerWidth', `${window.innerWidth}px`);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
};

// Condition pour les animations
export const shouldApplyEffect = (item: HTMLElement, windowWidth: number = window.innerWidth): boolean => {
    if (getComputedStyle(item).display === 'none') return false;

    const disableBreakpoint = item.dataset.effectDisable;
    if (disableBreakpoint === '') return false;

    const parsedBreakpoint = disableBreakpoint ? parseInt(disableBreakpoint, 10) : null;
    return !parsedBreakpoint || windowWidth >= parsedBreakpoint;

    /*
    La logique dans `shouldApplyEffect` :
    - `data-effect-disable=""` → retourne `false` (désactivé)
    - `data-effect-disable="768"` → retourne `false` si `windowWidth < 768`, `true` sinon
    - pas d'attribut → retourne `true` (toujours actif)
    */
};

// Get CSS var on root
export const headerHeight = (): number => {
    return -parseFloat(
        getComputedStyle(document.documentElement)
            .getPropertyValue('--header-height')
    ) - 40;
};

// BACK BUTTON
export const backButton = (): void => {
    const back = document.querySelector<HTMLElement>('.breadcrumb-backlink');
    if (!back) return;

    const handleBackClick = (e: Event): void => {
        e.preventDefault();
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = '/';
        }
    };

    back.removeEventListener('click', handleBackClick);
    back.addEventListener('click', handleBackClick);
};

// CONTACT PAGE BOTTOM
export const contactPageBottom = (): void => {
    const ctaFooterElement: HTMLElement | null = document.querySelector('.cta-footer');
    if (!ctaFooterElement) return;

    const elementHeight = ctaFooterElement.offsetHeight;
    const halfHeight = elementHeight / 2;

    document.documentElement.style.setProperty('--page-bottom-push', `${halfHeight}px`);
};
